import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import React from 'react';
import { json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import { getAllEnemies, getEnemiesByType, getEnemyTypes } from '~/lib/game-data.server';
import type { Enemy } from '~/types/game-data';
import { H1, H2 } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const typeFilter = url.searchParams.get('type');

  let enemies: Enemy[] = getAllEnemies();

  if (typeFilter) {
    enemies = getEnemiesByType(typeFilter);
  }

  const types = getEnemyTypes();

  return json({
    enemies,
    types,
    currentType: typeFilter,
  });
};

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Enemies Database',
    description: 'Study the Lumora bestiary including regions, weaknesses, and lore.',
  });

export default function EnemiesIndexPage() {
  const { enemies, types, currentType } = useLoaderData<typeof loader>();

  return (
    <Container size="default" padding="large" className="space-y-8">
      <header className="space-y-3">
        <Link
          to={routes.database.index()}
          className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          ← Back to Database
        </Link>
        <H1>Enemies Database</H1>
        <p className="text-lg text-muted-foreground">
          Explore {enemies.length} adversaries from the world of Lumora.
        </p>
      </header>

      {/* Filters */}
      {types.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4 text-lg">Filter by Type</H2>
          <div className="flex flex-wrap gap-2">
            <Link
              to={routes.database.enemies()}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                !currentType
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
              }`}
            >
              All Types
            </Link>
            {types.map((type) => (
              <Link
                key={type}
                to={`${routes.database.enemies()}?type=${encodeURIComponent(type)}`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  currentType === type
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary/10'
                }`}
              >
                {type}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Enemies List */}
      {enemies.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center">
          <p className="text-lg text-muted-foreground">
            No enemies found with the selected filters.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {enemies.map((enemy) => (
            <Link
              key={enemy.id}
              to={routes.database.enemies(enemy.id)}
              className="group block rounded-lg border border-border bg-card p-6 shadow transition hover:border-primary/30 hover:shadow-lg"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-500">
                  {enemy.role}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">{enemy.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                {enemy.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                {enemy.type_tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
                {enemy.type_tags.length > 3 && (
                  <span className="rounded-full border border-border bg-background px-2 py-1 text-xs text-muted-foreground">
                    +{enemy.type_tags.length - 3}
                  </span>
                )}
              </div>
              <span className="mt-3 inline-flex items-center text-sm font-semibold text-primary transition group-hover:translate-x-1">
                View Details →
              </span>
            </Link>
          ))}
        </div>
      )}
    </Container>
  );
}
